import { Invoice, MicroBilling } from "../../../pojo/Pricing";
import { OrgInvoiceCache } from "./org-invoice-cache";

/**
 * `billingCustom.json` takes every event at once and has no paging of its own, so an organization
 * with a year of events asks it for hundreds in a single read. These helpers break that into
 * batches of event ids and accumulate the answers, so the tab fills in as they arrive.
 */
export namespace OrgInvoiceBatches {
    /** Events per read. Small enough that no one read is slow, large enough not to flood the server. */
    export const SIZE = 550;

    export type Load = {
        invoices: Invoice[];
        loadedEvents: number;
        totalEvents: number;
        failedBatches: number;
        failure?: unknown;
    };

    export function empty(totalEvents: number): Load {
        return { invoices: [], loadedEvents: 0, totalEvents, failedBatches: 0 };
    }

    export function ofEventIds(eventIds: number[], size = SIZE): number[][] {
        const batches: number[][] = [];
        for (let i = 0; i < eventIds.length; i += size) batches.push(eventIds.slice(i, i + size));
        return batches;
    }

    /**
     * Invoices the caller already has, updated with the ones just read. A refetched event answers with
     * its invoices as they now stand, so those supersede the copies held from an earlier batch.
     */
    export function merge(existing: Invoice[], incoming: Invoice[]): Invoice[] {
        const incomingById = new Map(incoming.map((invoice) => [invoice.id, invoice]));
        const merged = existing.map((invoice) => incomingById.get(invoice.id) ?? invoice);
        const held = new Set(existing.map((invoice) => invoice.id));
        return merged.concat(incoming.filter((invoice) => !held.has(invoice.id)));
    }

    /**
     * Hands over whatever the cache already holds, then reads the rest a batch at a time, handing
     * the caller everything gathered so far after each one. The fetch is a parameter so the
     * batching can be exercised without an injector.
     *
     * A batch that fails is counted and passed over: one unreadable event should not cost the user
     * every other invoice the organization has. It is left out of the cache so it is tried again.
     */
    export async function walk(
        eventIds: number[],
        cache: OrgInvoiceCache,
        fetch: (eventIds: number[]) => Promise<MicroBilling>,
        emit: (load: Load) => void,
        abortSignal?: AbortSignal,
        size = SIZE,
    ): Promise<void> {
        const unread = cache.unread(eventIds);

        let load: Load = {
            ...empty(eventIds.length),
            invoices: cache.read(eventIds),
            loadedEvents: eventIds.length - unread.length,
        };

        if (load.loadedEvents) emit(load);

        for (const batch of ofEventIds(unread, size)) {
            if (abortSignal?.aborted) return;

            let invoices: Invoice[] = [];
            let failure: unknown;
            try {
                const billing = await fetch(batch);
                invoices = billing?.data?.items ?? [];
                cache.record(batch, billing);
            } catch (batchFailure) {
                failure = batchFailure ?? new Error("Unknown error");
            }

            // The resource this feeds is discarded on abort, and emitting into it would overwrite
            // whatever replaced it.
            if (abortSignal?.aborted) return;

            load = {
                invoices: merge(load.invoices, invoices),
                loadedEvents: load.loadedEvents + batch.length,
                totalEvents: load.totalEvents,
                failedBatches: load.failedBatches + (failure ? 1 : 0),
                failure: load.failure ?? failure,
            };
            emit(load);
        }
    }
}
